import { useState } from 'react';
import { DesignPreviewDialog } from './DesignTemplatePicker';
import { useResolvedDesignTemplate } from './useResolvedDesignTemplate';
import eyeIcon from '@app/assets/icons/eye.png';
import EmailIcon from '@app/assets/icons/mail-box.svg?react';
import IPhoneIcon from '@app/assets/icons/iphone.svg?react';
import { AutomationDesignInterface } from '@app/models/automations.ts';
import { LinkedMessageTemplate } from '@app/models';
import { templateToEmailContent, templateToMessageContent } from '@app/services/messageTemplates';
import { MessageTemplateChannel } from '@app/utils/consts';

// One channel of an automation/campaign "Send via" card: which design it sends (custom, linked template or business default)
// with a preview. Campaigns don't store their custom design, so for them only the link is shown
export const DesignTemplateSummary = ({
  channel,
  linkedTemplate,
  customDesign,
}: {
  channel: MessageTemplateChannel;
  linkedTemplate?: LinkedMessageTemplate | null;
  customDesign?: AutomationDesignInterface;
}) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const isEmail = channel === MessageTemplateChannel.EMAIL;
  const customContent = customDesign ? (isEmail ? templateToEmailContent(customDesign) : templateToMessageContent(customDesign)) : null;
  const design = useResolvedDesignTemplate(channel, linkedTemplate?.id ?? null, customContent);

  // The linked name comes from the automation/campaign itself, so it's right even when the template isn't in the picker's first 100
  const templateName = linkedTemplate?.name ?? design.selectedLabel;
  const Icon = isEmail ? EmailIcon : IPhoneIcon;

  return (
    <div className="flex items-center gap-2">
      <div className="w-4 h-4 rounded flex items-center justify-center">
        <Icon />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-base-coolGray">{isEmail ? 'EMAIL TEMPLATE' : 'TEXT TEMPLATE'}</div>
        <div className="text-xs truncate">
          {customContent ? (
            <>
              Custom design <span className="text-base-coolGray">(based on {linkedTemplate?.name ?? 'Business default'})</span>
            </>
          ) : (
            templateName
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={() => setIsPreviewOpen(true)}
        className="bg-[#E7EAF4] rounded-md pl-2 pr-4 py-2 text-sm flex items-center justify-start min-w-[80px] hover:bg-[#D1D5DB] transition-colors shrink-0"
      >
        <img src={eyeIcon} alt="" className="w-4 h-4 mr-2" />
        Preview
      </button>

      <DesignPreviewDialog
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        title={customContent ? design.title : templateName}
        channel={channel}
        content={design.content}
      />
    </div>
  );
};
